// Small helper that computes an inclusive-start / exclusive-end date range for a
// selected period label. Used by Front End Sales and All Sales pages to filter rows.
// All dates are compared as "YYYY-MM-DD" ISO strings, so the helper returns the same.

export const PERIOD_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "last_week", label: "Last week" },
  { value: "last_month", label: "Last month" },
  { value: "last_quarter", label: "Last quarter" },
  { value: "last_year", label: "Last year" },
];

// Local calendar date — NOT toISOString(), which shifts to UTC and turns 1 Oct 00:00 IST
// into "2026-09-30", making "This month" include the last day of the previous month.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Week starts Monday (matching most Ops reports we've already shipped).
const startOfWeek = (d) => {
  const day = d.getDay();       // 0=Sun ... 6=Sat
  const diff = (day + 6) % 7;   // days since Monday
  const out = new Date(d);
  out.setDate(d.getDate() - diff);
  out.setHours(0, 0, 0, 0);
  return out;
};

const addDays = (d, n) => { const out = new Date(d); out.setDate(d.getDate() + n); return out; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/**
 * Given a period key, return { start, end } as ISO date strings.
 * `start` is inclusive, `end` is exclusive so callers can do `date >= start && date < end`.
 * Returns null for "all" — callers should treat that as "no date filter".
 */
export const periodRange = (period, now = new Date()) => {
  if (!period || period === "all") return null;
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  switch (period) {
    case "this_week": {
      const s = startOfWeek(today);
      return { start: iso(s), end: iso(addDays(s, 7)) };
    }
    case "last_week": {
      const s = addDays(startOfWeek(today), -7);
      return { start: iso(s), end: iso(addDays(s, 7)) };
    }
    case "this_month": {
      const s = startOfMonth(today);
      return { start: iso(s), end: iso(addMonths(s, 1)) };
    }
    case "last_month": {
      const s = addMonths(startOfMonth(today), -1);
      return { start: iso(s), end: iso(addMonths(s, 1)) };
    }
    case "this_quarter": {
      const s = startOfQuarter(today);
      return { start: iso(s), end: iso(addMonths(s, 3)) };
    }
    case "last_quarter": {
      const s = addMonths(startOfQuarter(today), -3);
      return { start: iso(s), end: iso(addMonths(s, 3)) };
    }
    case "this_year": {
      const s = startOfYear(today);
      return { start: iso(s), end: iso(new Date(s.getFullYear() + 1, 0, 1)) };
    }
    case "last_year": {
      const s = new Date(today.getFullYear() - 1, 0, 1);
      return { start: iso(s), end: iso(new Date(s.getFullYear() + 1, 0, 1)) };
    }
    default:
      return null;
  }
};

/** Today as YYYY-MM-DD in the gym's timezone (e.g. "Asia/Dubai"); falls back to the
 *  browser's own timezone if none is given. Never UTC. */
export const todayLocal = (timeZone) => {
  if (timeZone) {
    try {
      // en-CA formats as YYYY-MM-DD
      return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    } catch { /* unknown zone → fall through */ }
  }
  return iso(new Date());
};

/** The calendar month containing an ISO date, as a custom { from, to } range. */
export const monthRangeOf = (isoDate) => {
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`);
  return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
};

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
/** "September 2026" for an ISO date. */
export const monthLabelOf = (isoDate) => {
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * Convenience: given a row and a period, return true if the row's date lies inside.
 * Row date can be a bare ISO date ("2026-02-24") or a full ISO datetime — we slice.
 * Rows without a `date` field are included (better to show than hide unknown-date rows).
 */
export const rowInPeriod = (rowDate, period, custom) => {
  // period === "custom": `custom` is { from, to } (inclusive ISO dates, either may be blank).
  if (period === "custom") {
    if (!rowDate) return true;
    const d = String(rowDate).slice(0, 10);
    return (!custom?.from || d >= custom.from) && (!custom?.to || d <= custom.to);
  }
  const range = periodRange(period);
  if (!range) return true;
  if (!rowDate) return true;
  const iso10 = String(rowDate).slice(0, 10);
  return iso10 >= range.start && iso10 < range.end;
};
